<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import shareIcon from '@/assets/images/ide/share-project-icon.svg'
import regenerate from '@/assets/images/ide/arrow-refresh.svg'

import { AUTHMODELHS } from '@/utils/models'
import { useIdeStore } from '@/stores/ide.store'

import { GENERIC_TYPE, type IHttpStatus } from '@/services/ide/projects.service'
import shareService from '@/services/ide/settings/share.service'
import Button from '@/components/atoms/button/index.vue'
import SideBarTooltip from '@/components/ide/ide/SettingsSideBarTooltip.vue'
import utilModelsService from '@/services/util.models.service'

const ideStore = useIdeStore()
const route = useRoute()

const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpStatus = ref<IHttpStatus>({ status: '', message: '' })
const loadUrlsInProgress = ref<boolean>(false)

const projectUrl = ref<string | null>(null)

const copiedToClip = ref<boolean>(false)
const showCopiedMessageText = ref<string>('')
const copySuccess = ref<boolean>(false)
const timeOut = ref<NodeJS.Timeout | number | null>(null)

const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})
/**
 * Get the share url
 * @param reGenerate - to regenerate the link again
 */
const share = async (reGenerate: boolean = false) => {
  if (!reGenerate) projectUrl.value = null
  loadUrlsInProgress.value = true

  await shareService
    .instantShare()
    .then((response) => {
      projectUrl.value = response?.data?.url || null
      httpStatus.value = {
        status: GENERIC_TYPE.SUCCESS,
        message: `Snapshot of your code is created. Copy the URL${
          !isAdvanced.value ? 's' : ''
        } immediately as every instant share creates a new copy of the program with a new URL.`
      }
    })
    .catch((error) => {
      httpStatus.value = {
        status: GENERIC_TYPE.ERROR,
        message: error?.response?.data?.message || 'Something went wrong. Please try again later.'
      }
    })

  loadUrlsInProgress.value = false
}

/**
 * Copy to clipboard
 * @param url string
 */
const copyToClipboard = async (url: string) => {
  window.navigator.clipboard.writeText(url).then(
    () => {
      showCopiedMessageText.value = 'Copied to clipboard!'
      copySuccess.value = true
      copiedToClip.value = true
    },
    () => {
      showCopiedMessageText.value = 'Copy to clipboard failed!'
      copySuccess.value = false
      copiedToClip.value = true
    }
  )
  if (timeOut.value) clearTimeout(timeOut.value)
  timeOut.value = setTimeout(() => {
    copiedToClip.value = false
    copySuccess.value = false
    showCopiedMessageText.value = ''
  }, 2000)
}

/**
 * Initialize the copy editor
 */
const init = () => {
  projectUrl.value = null
}

/**
 * @description to open login modal
 */
const openLoginModal = () => {
  utilModelsService.openModal(AUTHMODELHS.LOGIN)
  ideStore.setSettingPopupHs(null)
}

onMounted(() => {
  init()
  watch(route, () => {
    httpStatus.value = { status: '', message: '' }
    init()
  })
  watch(httpStatus, () => {
    if (httpStatus.value) {
      if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
      httpSuccessTimeOut.value = setTimeout(() => {
        httpStatus.value = { status: '', message: '' }
      }, 5000)
    }
  })
})
</script>

<template>
  <div
    class="flex h-full w-full flex-col gap-2 px-2 py-3 sm:max-w-[80vw] sm:px-8 lg:max-w-full lg:py-2"
  >
    <div
      class="flex w-full flex-col-reverse items-center justify-between gap-3 sm:flex-row sm:gap-10"
    >
      <div class="flex w-full flex-col gap-3">
        <span class="text-high text-sm font-normal leading-5"
          >You're not signed in, so you can only create an <strong>Instant Share link</strong>.
          <br />
          This is a view-only link to your project’s current version. It does not display any new
          changes.</span
        >
        <span class="text-sm font-semibold">Sign in to unlock these features:</span>
        <div class="text-high text-sm font-semibold">
          <ol class="list-inside list-decimal font-normal leading-5">
            <li>{{ ' ' }}Share & collaborate on projects in real-time.</li>
            <li>{{ ' ' }}Manage project access & permissions.</li>
            <li>{{ ' ' }}Create & fork unlimited versions.</li>
          </ol>
        </div>
      </div>
      <img :src="shareIcon" alt="" class="mr-8 h-28 w-28" />
    </div>
    <div class="py-6">
      <div
        v-if="!projectUrl"
        class="flex w-full flex-wrap items-center justify-center gap-3 sm:gap-14"
      >
        <Button variant="outline" size="small" color="secondary" @click="share">{{
          loadUrlsInProgress ? 'Loading...' : ' Instant share URL'
        }}</Button>
        <Button variant="filled" size="small" color="secondary" @click="openLoginModal"
          >Sign in to JDoodle</Button
        >
      </div>
      <div class="relative flex w-full items-center" v-else>
        <div class="hs-tooltip flex w-full overflow-hidden rounded-r-lg">
          <input
            class="section-new-primary text-mid w-full rounded-l-lg border border-none p-2 text-sm font-light outline-none"
            v-model="projectUrl"
            disabled
          />

          <button :disabled="loadUrlsInProgress" @click="share(true)">
            <img
              :src="regenerate"
              alt=""
              class="hs-tooltip-toggle absolute right-28 top-3 h-4 w-4"
              :class="{ 'rotate-infinite': loadUrlsInProgress }"
            />
          </button>
          <SideBarTooltip content="Regenerate new instant share link" />
          <Button
            variant="filled"
            @click="copyToClipboard(projectUrl)"
            class="mx-auto w-fit whitespace-nowrap rounded-none"
            type="submit"
          >
            Copy URL
          </Button>
        </div>
      </div>
      <p
        :class="[
          'p-xsmall pt-2 ',
          { success: copySuccess, error: !copySuccess, hidden: !copiedToClip }
        ]"
      >
        {{ showCopiedMessageText }}
      </p>
    </div>
  </div>
</template>
<style scoped>
.rotate-infinite {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
